import BridgeSettings from '../components/whatsapp/BridgeSettings';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { whatsappAPI } from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import {
  ExclamationTriangleIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import ConfirmModal from '../components/ConfirmModal';

interface WhatsAppConfigForm {
  whatsapp_token: string;
  phone_number_id: string;
  verify_token: string;
  app_secret: string;
  business_id: string;
  display_name: string;
  phone_number: string;
  is_active: boolean;
}

const WhatsAppSettings = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tokenChanged, setTokenChanged] = useState(false);
  const [secretChanged, setSecretChanged] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isAdmin = user?.is_host || user?.role === 'admin';

  const { data: configData, isLoading } = useQuery({
    queryKey: ['whatsapp-config'],
    queryFn: () => whatsappAPI.getConfig(),
    select: (res) => res.data.data,
    enabled: isAdmin,
  });

  const { data: webhookData } = useQuery({
    queryKey: ['whatsapp-webhook-url'],
    queryFn: () => whatsappAPI.getWebhookUrl(),
    select: (res) => res.data.data,
    enabled: isAdmin,
  });

  const { register, handleSubmit, formState: { errors, isDirty }, watch } = useForm<WhatsAppConfigForm>({
    values: configData ? {
      whatsapp_token: '',
      phone_number_id: configData.phone_number_id || '',
      verify_token: configData.verify_token || '',
      app_secret: '',
      business_id: configData.business_id || '',
      display_name: configData.display_name || '',
      phone_number: configData.phone_number || '',
      is_active: configData.is_active,
    } : {
      whatsapp_token: '',
      phone_number_id: '',
      verify_token: '',
      app_secret: '',
      business_id: '',
      display_name: '',
      phone_number: '',
      is_active: false,
    },
  });

  const isActive = watch('is_active');

  const saveMutation = useMutation({
    mutationFn: (formData: WhatsAppConfigForm) => {
      const payload: Record<string, unknown> = {};
      if (formData.whatsapp_token) payload.whatsapp_token = formData.whatsapp_token;
      if (formData.app_secret) payload.app_secret = formData.app_secret;
      payload.phone_number_id = formData.phone_number_id || undefined;
      payload.verify_token = formData.verify_token || undefined;
      payload.business_id = formData.business_id || undefined;
      payload.display_name = formData.display_name || undefined;
      payload.phone_number = formData.phone_number || undefined;
      payload.is_active = formData.is_active;
      return whatsappAPI.updateConfig(payload as any);
    },
    onSuccess: () => {
      toast.success('Configurazione WhatsApp salvata');
      queryClient.invalidateQueries({ queryKey: ['whatsapp-config'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-webhook-url'] });
      setTokenChanged(false);
      setSecretChanged(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => whatsappAPI.deleteConfig(),
    onSuccess: () => {
      toast.success('Configurazione WhatsApp eliminata');
      queryClient.invalidateQueries({ queryKey: ['whatsapp-config'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    },
  });

  const handleCopyWebhookUrl = () => {
    if (webhookData?.webhook_url) {
      navigator.clipboard.writeText(webhookData.webhook_url);
      setCopied(true);
      toast.success('URL copiato negli appunti');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Accesso negato. Solo gli amministratori possono accedere a questa pagina.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configurazione WhatsApp</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configura l'integrazione WhatsApp Cloud API per il tuo tenant.
        </p>
      </div>

      <BridgeSettings />

      {/* Status badges */}
      <div className="flex items-center space-x-2 flex-wrap gap-y-2">
        {configData && (
          <>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              configData.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {configData.is_active ? 'Attivo' : 'Inattivo'}
            </span>
            {configData.whatsapp_token_set && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Token configurato
              </span>
            )}
            {configData.app_secret_set && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                App Secret configurato
              </span>
            )}
            {configData.webhook_verified_at && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Webhook verificato: {new Date(configData.webhook_verified_at).toLocaleString('it-IT')}
              </span>
            )}
          </>
        )}
      </div>

      {/* Webhook URL info box */}
      {webhookData && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Webhook URL per Meta Developer Console</h3>
          <div className="flex items-center space-x-2">
            <code className="flex-1 text-sm bg-white border border-blue-200 rounded px-3 py-2 text-blue-900 break-all">
              {webhookData.webhook_url}
            </code>
            <button
              onClick={handleCopyWebhookUrl}
              className="inline-flex items-center px-3 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {copied ? (
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
              ) : (
                <ClipboardDocumentIcon className="h-5 w-5" />
              )}
            </button>
          </div>
          {webhookData.verify_token && (
            <p className="mt-2 text-xs text-blue-700">
              Verify Token: <code className="bg-white px-1 py-0.5 rounded">{webhookData.verify_token}</code>
            </p>
          )}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit((d) => saveMutation.mutate(d))} className="bg-white shadow rounded-lg">
        <div className="p-6 space-y-6">
          {/* WhatsApp Token */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              WhatsApp Token
            </label>
            <input
              type="password"
              {...register('whatsapp_token')}
              onChange={(e) => {
                register('whatsapp_token').onChange(e);
                setTokenChanged(!!e.target.value);
              }}
              placeholder={configData?.whatsapp_token_set ? '(token configurato - lascia vuoto per non modificare)' : 'Inserisci il token WhatsApp Cloud API'}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Token di accesso permanente dalla Meta Developer Console.
            </p>
          </div>

          {/* Phone Number ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number ID
            </label>
            <input
              type="text"
              {...register('phone_number_id')}
              placeholder="Es. 123456789012345"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            {errors.phone_number_id && (
              <p className="mt-1 text-sm text-red-600">{errors.phone_number_id.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              ID del numero di telefono WhatsApp Business dalla Meta Developer Console.
            </p>
          </div>

          {/* Verify Token */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Verify Token
            </label>
            <input
              type="text"
              {...register('verify_token')}
              placeholder="Token di verifica per il webhook"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Token personalizzato per la verifica del webhook. Deve corrispondere a quello inserito nella Meta Developer Console.
            </p>
          </div>

          {/* App Secret */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              App Secret
            </label>
            <input
              type="password"
              {...register('app_secret')}
              onChange={(e) => {
                register('app_secret').onChange(e);
                setSecretChanged(!!e.target.value);
              }}
              placeholder={configData?.app_secret_set ? '(secret configurato - lascia vuoto per non modificare)' : 'App Secret dalla Meta Developer Console'}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              App Secret per la validazione HMAC-SHA256 delle richieste webhook.
            </p>
          </div>

          {/* Business ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business ID
            </label>
            <input
              type="text"
              {...register('business_id')}
              placeholder="Es. 987654321098765"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              ID del WhatsApp Business Account.
            </p>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome Visualizzato
            </label>
            <input
              type="text"
              {...register('display_name')}
              placeholder="Nome del bot WhatsApp"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numero di Telefono
            </label>
            <input
              type="text"
              {...register('phone_number')}
              placeholder="Es. +39 123 456 7890"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Numero di telefono WhatsApp Business (informativo).
            </p>
          </div>

          {/* Is Active toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Attiva integrazione
              </label>
              <p className="text-xs text-gray-500">
                Abilita la ricezione dei messaggi WhatsApp.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                {...register('is_active')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          {/* Activation warning */}
          {isActive && !configData?.webhook_verified_at && (
            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">Attenzione</h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    Prima di attivare, assicurati di aver configurato il webhook URL nella Meta Developer Console
                    e che la verifica sia avvenuta con successo.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Token/secret change warning */}
          {(tokenChanged || secretChanged) && (
            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">Attenzione</h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    Stai modificando le credenziali di accesso. Assicurati che i nuovi valori siano corretti.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-3 flex justify-between rounded-b-lg">
          {configData && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
            </button>
          )}
          <div className={!configData ? 'ml-auto' : ''}>
            <button
              type="submit"
              disabled={saveMutation.isPending || (!isDirty && !tokenChanged && !secretChanged)}
              className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveMutation.isPending ? 'Salvataggio...' : 'Salva configurazione'}
            </button>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          deleteMutation.mutate();
          setShowDeleteConfirm(false);
        }}
        title="Conferma Eliminazione"
        message="Sei sicuro di voler eliminare la configurazione WhatsApp?"
        confirmLabel="Elimina"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

export default WhatsAppSettings;
